import { t, Trans } from '@lingui/macro'
import { Form, Formik, FormikValues } from 'formik'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { object, string } from 'yup'

import { Button, Checkbox, Heading, Radio, Space, Textarea } from '@/components'
import { IconInfoCircle } from '@/components/Icons'
import MutationErrors from '@/compositions/MutationErrors'
import { ErrorMessages, Routes, Storage } from '@/constants'
import { useApplyToCohortMutation, UserError } from '@/graphql/generated'
import useAuth from '@/hooks/useAuth'
import { formatDate } from '@/utils/date'

import { ComponentTypes } from '../types'

const textareaProps = {
  rows: 3,
  resizable: false,
}

const APPLICATION_FORM_QUESTION_ID_STRING = 'questionId'

const Registration: React.FC<React.PropsWithChildren<ComponentTypes>> = ({
  applicationForm,
  user,
  prevStep,
  nextStep,
}) => {
  const { query, replace } = useRouter()
  const [applyToCohort, { loading: applyingToCohort }] = useApplyToCohortMutation()
  const { logOut } = useAuth()

  const [mutationErrors, setMutationErrors] = useState<UserError[]>()
  const [initialValues, setInitialValues] = useState({
    cohortId: '',
    acceptedCreatorReachOutOptIn: false,
  })
  const [formSchema, setFormSchema] = useState({
    cohortId: string().required(ErrorMessages.REQUIRED_FIELD),
  })

  const onLogoutSuccess = (): void => {
    prevStep()
  }

  const handleSubmit = async (values: FormikValues): Promise<void> => {
    const questions = Object.keys(values)
      .filter(value => value.includes(APPLICATION_FORM_QUESTION_ID_STRING))
      .map(value => value.split('_')[1])

    const variables = {
      cohortId: values.cohortId,
      applicant: {
        acceptedCreatorReachOutOptIn: values.acceptedCreatorReachOutOptIn,
        answers: questions.map(questionId => ({
          questionId: questionId ?? '',
          question: applicationForm?.applicationForm?.questions.find(({ id }) => id === questionId)?.question ?? '',
          answer: values[`${APPLICATION_FORM_QUESTION_ID_STRING}_${questionId}`] ?? '',
        })),
        urlTrackingParams: getUrlTrackingParams(),
      },
    }

    // We have two scenarios here:
    // 1. Make the mutation right away when there's a `user`.
    if (user?.me) {
      const { data } = await applyToCohort({ variables })

      if (data?.applyToCohort?.errors) {
        return setMutationErrors(data?.applyToCohort?.errors)
      }

      const applicantId = data?.applyToCohort?.applicant?.id

      replace(`/${query.slug}${Routes.SUCCESSFUL_APPLY}?applicant=${applicantId}`)
      return
    }

    // 2. Save data on `localStorage` to be used on the next step (Account section).
    localStorage.setItem(Storage.APPLY_APPLICATION_DATA, JSON.stringify(variables))

    nextStep()
  }

  useEffect(() => {
    const localStorageData = localStorage.getItem(Storage.APPLY_APPLICATION_DATA)

    if (localStorageData) {
      setInitialValues(JSON.parse(localStorageData))
    }
  }, [])

  useEffect(() => {
    const questions = applicationForm?.applicationForm?.questions
    const cohortId = applicationForm?.applicationForm?.cohorts?.length
      ? applicationForm.applicationForm.cohorts[0].id
      : ''

    if (questions?.length) {
      setFormSchema({
        ...formSchema,
        ...questions.reduce(
          (acc, el) =>
            Object.assign({}, acc, {
              [`${APPLICATION_FORM_QUESTION_ID_STRING}_${el.id}`]: el.isRequired
                ? string().required(ErrorMessages.REQUIRED_FIELD)
                : string(),
            }),
          {},
        ),
      })

      setInitialValues({
        ...initialValues,
        cohortId,
        ...questions.reduce(
          (acc, el) =>
            Object.assign({}, acc, {
              [`${APPLICATION_FORM_QUESTION_ID_STRING}_${el.id}`]: '',
            }),
          {},
        ),
      })
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationForm])

  const getUrlTrackingParams = (): string | undefined => {
    const hasUrlParams = Object.keys(query).length > 1 // slug param is always there

    if (hasUrlParams) {
      return JSON.stringify(query)
    }

    // if there's no URL params return undefined since we don't want to send anything to the API in that case
  }

  const applicationFormQuestions = applicationForm?.applicationForm?.questions

  return (
    <>
      {user?.me ? (
        <div className="mb-8 flex items-center rounded-lg bg-white px-4 py-2 text-grey-dark">
          <div className="flex items-center">
            <IconInfoCircle className="icon-sm mr-2" />
            <div className="italic font-sans text-base-none capsize">
              <Trans>Logged in as {user.me?.email}</Trans>
            </div>
          </div>

          <div className="grow text-right">
            <button
              onClick={() => logOut(null, { onFinish: onLogoutSuccess })}
              className="font-medium underline hover:text-navy-dark"
            >
              {t`Log out`}
            </button>
          </div>
        </div>
      ) : null}

      <MutationErrors errors={mutationErrors} />

      <Formik
        initialValues={initialValues}
        validationSchema={object().shape(formSchema)}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ isSubmitting }) => {
          const isLoading = isSubmitting || applyingToCohort

          return (
            <Form>
              <Space y={12}>
                <div>
                  <Space y={3}>
                    <Heading level={3} variant={TextStyle => TextStyle.MEDIUM_LG_NORMAL}>
                      {t`Select the cohort`}
                    </Heading>

                    {applicationForm?.applicationForm?.cohorts?.map(({ id, name, schedule }) => (
                      <Radio
                        key={id}
                        name="cohortId"
                        label={name}
                        description={`Starts ${formatDate(schedule?.startDate, { month: 'long', day: 'numeric' })}`}
                        value={id}
                      />
                    ))}
                  </Space>
                </div>

                {applicationFormQuestions?.length ? (
                  <div>
                    <Space y={6}>
                      <Heading level={3} variant={TextStyle => TextStyle.MEDIUM_LG_NORMAL}>
                        {t`Your registration`}
                      </Heading>

                      <Space y={10}>
                        {[...applicationFormQuestions]
                          .sort((a, b) => a.position - b.position)
                          .map(({ id, isRequired, question }) => (
                            <Textarea
                              key={id}
                              name={`${APPLICATION_FORM_QUESTION_ID_STRING}_${id}`}
                              label={question}
                              {...(!isRequired ? { labelHelper: '(optional)' } : '')}
                              {...textareaProps}
                            />
                          ))}
                      </Space>
                    </Space>
                  </div>
                ) : null}

                <div>
                  {applicationForm?.applicationForm?.learningCommunity.creatorReachOutOptInText?.length ? (
                    <Space y={6}>
                      <Heading level={3} variant={TextStyle => TextStyle.MEDIUM_LG_NORMAL}>
                        {t`Communication from the creator`}
                      </Heading>

                      <Checkbox
                        name="acceptedCreatorReachOutOptIn"
                        label={applicationForm?.applicationForm?.learningCommunity.creatorReachOutOptInText}
                      />
                    </Space>
                  ) : null}
                </div>

                <Button type="submit" variant="primary" disabled={isLoading} loading={isLoading} block>
                  {isLoading ? t`Please wait ...` : user?.me ? t`Register` : t`Continue`}
                </Button>
              </Space>
            </Form>
          )
        }}
      </Formik>
    </>
  )
}

export default Registration
